/**
 * PE delegated portfolio viewing (design §18): persisted child tenant id + header name for API calls.
 */

/**
 * **Product freeze (April 2026):** Delegated “view as portfolio company” is **disabled** in the shell until
 * hierarchy-scoped reads are verified end-to-end. When `false`: navbar dropdown is hidden, API clients do not
 * send the delegated header, and Layout clears persisted context once on load. Set to `true` to re-enable.
 * @see docs/Capabilities/core/access-management/design/PE_PORTFOLIO_OPERATIONS_DESIGN.md §18.12
 */
export const PE_DELEGATED_SHELL_UI_ENABLED = false;

export const PE_DELEGATED_CHILD_STORAGE_KEY = 'tmos_pe_delegated_child_tenant_id';

/** Must match backend `PE_DELEGATED_CHILD_TENANT_HEADER` casing as sent on the wire. */
export const PE_DELEGATED_CHILD_HEADER = 'X-TMOS-PE-Delegated-Child-Tenant-Id';

/** Session-only PE “view as company” for the Calendar screen (does not enable global delegated shell). */
const CALENDAR_PE_VIEW_SESSION_KEY = 'tmos_calendar_pe_view_child_tenant_id';

export function getCalendarViewChildTenantId(): string | null {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    const v = sessionStorage.getItem(CALENDAR_PE_VIEW_SESSION_KEY);
    return v != null && v.trim() !== '' ? v.trim() : null;
  } catch {
    return null;
  }
}

export function setCalendarViewChildTenantId(id: string | null): void {
  try {
    if (typeof sessionStorage === 'undefined') return;
    if (id == null || id === '') {
      sessionStorage.removeItem(CALENDAR_PE_VIEW_SESSION_KEY);
    } else {
      sessionStorage.setItem(CALENDAR_PE_VIEW_SESSION_KEY, id);
    }
  } catch {
    /* ignore */
  }
}

type Listener = () => void;
const listeners: Listener[] = [];

export function subscribePeDelegatedContext(listener: Listener): () => void {
  listeners.push(listener);
  return () => {
    const i = listeners.indexOf(listener);
    if (i >= 0) {
      listeners.splice(i, 1);
    }
  };
}

function emit(): void {
  for (const l of listeners) {
    try {
      l();
    } catch {
      /* ignore subscriber errors */
    }
  }
}

export function getPeDelegatedChildTenantId(): string | null {
  if (!PE_DELEGATED_SHELL_UI_ENABLED) {
    return null;
  }
  const v = localStorage.getItem(PE_DELEGATED_CHILD_STORAGE_KEY);
  if (v == null || v.trim() === '') {
    return null;
  }
  return v.trim();
}

/** Updates storage and notifies subscribers (does not call the audit API). */
export function setPeDelegatedChildTenantId(id: string | null): void {
  if (!PE_DELEGATED_SHELL_UI_ENABLED && id != null && id !== '') {
    return;
  }
  if (id == null || id === '') {
    localStorage.removeItem(PE_DELEGATED_CHILD_STORAGE_KEY);
  } else {
    localStorage.setItem(PE_DELEGATED_CHILD_STORAGE_KEY, id);
  }
  emit();
}

export function clearPeDelegatedChildTenantId(): void {
  setPeDelegatedChildTenantId(null);
}
